import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { storeSession } from '../../utils/authUtils';
import { trackSignup } from '../../utils/analytics';
import { getCountryOptions } from '../../utils/countries';
import { STATUSES, MIN_INTERESTS } from '../../constants/doctorProfile';
import InterestPicker from '../InterestPicker';

const PageContainer = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

const Card = styled.div`
  max-width: 480px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h2`
  margin-top: 0;
  text-align: center;
  font-size: 1.7rem;
  font-weight: 800;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: #4b5563;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const FormColumn = styled.div`
  flex: 1 1 200px;
  min-width: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 3px;
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 0px;
  font-size: 0.9rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 0px;
  font-size: 0.9rem;
`;

const SubmitButton = styled.button`
  background-color: #47ABDB;
  color: white;
  border: none;
  padding: 10px 20px;
  width: 100%;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 0px;
  margin-top: 0.5rem;

  &:hover { background-color: #0056b3; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 0px;
  text-align: center;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const SectionTitle = styled.h3`
  margin: 0.5rem 0 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
`;

const Checks = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const CheckLabel = styled.label`
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
  font-weight: normal;
  font-size: 0.85rem;
`;


// Same options RegistrationForm.js offers on the public site, kept in sync
// manually - both are small, self-contained components in this codebase.
const titleOptions = {
  en: ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.', 'No title'],
  de: ['Herr', 'Frau', 'Dr.', 'Prof.', 'keine Angabe'],
};
const titleMapping = { 'Herr': 'Mr.', 'Frau': 'Ms.', 'Dr.': 'Dr.', 'Prof.': 'Prof.', 'keine Angabe': 'No title' };
const specializationOptions = {
  en: ['Non Medical', 'Anesthesia', 'Cardiac Surgery', 'Cardiology', 'General Practitioner (GP)', 'Trauma and Orthopedics', 'Radiology', 'Dermatology', 'Endocrinology', 'Gastroenterology', 'Hematology', 'Immunology', 'Nephrology', 'Neurology', 'Obstetrics and Gynecology (OB/GYN)', 'Oncology', 'Ophthalmology', 'Otolaryngology (ENT)', 'Pediatrics', 'Psychiatry', 'Pulmonology', 'Rheumatology', 'Surgery', 'Urology', 'Other'],
  de: ['Nicht-Medizinisch', 'Allgemeinmedizin', 'Anästhesie', 'Augenheilkunde', 'Chirurgie', 'Dermatologie', 'Endokrinologie', 'Gastroenterologie', 'Geburtshilfe und Gynäkologie', 'Hals-Nasen-Ohren-Heilkunde', 'Hämatologie', 'Herzchirurgie', 'Immunologie', 'Kardiologie', 'Nephrologie', 'Neurologie', 'Onkologie', 'Pädiatrie', 'Psychiatrie', 'Pneumologie', 'Radiologie', 'Rheumatologie', 'Unfallchirurgie und Orthopädie', 'Urologie', 'Andere'],
};

const emptyProfile = {
  title: '', firstName: '', lastName: '', phoneNumber: '00000000000',
  institution: '', specialty: '', streetAddress: '',
  city: '', state: '', postalCode: '', country: '',
  photoUrl: '', linkedInUrl: '', facebookUrl: '', instagramUrl: '', websiteUrl: '',
  shortBioDe: '', shortBioEn: '',
  professionalStatuses: ['PARTICIPANT'], interests: [],
};

// Verify-first signup: step 1 (no token in the URL) always does the same
// thing no matter what's true about the email - see
// AttendeeAccountService.startSignup for why. Step 2 (landed here via the
// emailed link, ?token=...) is the only point where it's safe to reveal
// whether the email is known to us, or any data associated with it, since
// token possession IS the proof of ownership.
const AttendeeSignup = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('eventId');
  const signupToken = searchParams.get('token');

  // 'email' (start) -> 'sent' (generic ack, check your inbox)
  // 'resolving' (token present, checking it) -> 'claim' | 'fresh' | 'pendingSpeaker' | 'exists' | 'invalidToken'
  // then a successful claim/fresh/pendingSpeaker navigates to /signup/success.
  const [step, setStep] = useState(signupToken ? 'resolving' : 'email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [profile, setProfile] = useState(emptyProfile);
  const [consentGiven, setConsentGiven] = useState(false);
  const [mediaConsentGiven, setMediaConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const privacyPolicyUrl = `${apiUrl}/uploads/privacy_policy_${language === 'de' ? 'de' : 'en'}.pdf`;

  useEffect(() => {
    if (!signupToken) return;
    const resolve = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/api/attendee/public/signup/status`, { params: { token: signupToken } });
        setEmail(data.email || '');
        if (data.status === 'ACCOUNT_EXISTS') {
          setStep('exists');
        } else if (data.status === 'PENDING_SPEAKER_INVITE') {
          // A pending speaker invitation (see AttendeeAccountService.
          // activatePendingAccount) has no prefill - it's the same empty
          // profile as 'fresh', just submitted to a different endpoint that
          // activates that same account instead of creating a new one.
          setStep('pendingSpeaker');
        } else if (data.status === 'UNCLAIMED_DATA_FOUND') {
          if (data.prefill) {
            setProfile({
              title: data.prefill.title || '',
              firstName: data.prefill.firstName || '',
              lastName: data.prefill.lastName || '',
              phoneNumber: data.prefill.phoneNumber || '00000000000',
              institution: data.prefill.institution || '',
              specialty: data.prefill.specialty || '',
              streetAddress: data.prefill.streetAddress || '',
              city: data.prefill.city || '',
              state: data.prefill.state || '',
              postalCode: data.prefill.postalCode || '',
              country: data.prefill.country || '',
              photoUrl: data.prefill.photoUrl || '',
              linkedInUrl: data.prefill.linkedInUrl || '',
              facebookUrl: data.prefill.facebookUrl || '',
              instagramUrl: data.prefill.instagramUrl || '',
              websiteUrl: data.prefill.websiteUrl || '',
              shortBioDe: data.prefill.shortBioDe || '',
              shortBioEn: data.prefill.shortBioEn || '',
              professionalStatuses: data.prefill.professionalStatuses || ['PARTICIPANT'],
              interests: data.prefill.interests || [],
            });
          }
          setStep('claim');
        } else {
          setStep('fresh');
        }
      } catch (err) {
        setStep('invalidToken');
      }
    };
    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupToken]);

  const handleStartSignup = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await axios.post(`${apiUrl}/api/attendee/public/signup/start`, { email, eventId: eventId || undefined });
      setStep('sent');
    } catch (err) {
      setError(t('signup.checkEmailError', 'Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFreshChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const toggleListField = (key, value) => {
    setProfile((current) => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter((v) => v !== value)
        : [...(current[key] || []), value],
    }));
  };

  const roleLabel = (status) => t(`speaker.role.${status}`, status.replaceAll('_', ' '));

  // Shared by both 'claim' (returning legacy users - see AttendeeSignup's
  // effect above, prefilled from whatever the old public form collected) and
  // 'fresh' (brand-new users): same required-field wall for both, since a
  // legacy User row may be missing fields the old form never asked for. See
  // AttendeeAccountService.requireCompleteProfile.
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consentGiven) {
      setError(t('consent.required', 'You must agree to the data processing to register.'));
      return;
    }
    if (!mediaConsentGiven) {
      setError(t('mediaConsent.required', 'You must acknowledge the photo/video notice to register.'));
      return;
    }
    if ((profile.interests || []).length < MIN_INTERESTS) {
      setError(t('speaker.onboarding.err.interests', 'Please select at least three cardiac interests.'));
      return;
    }
    setIsLoading(true);
    try {
      const payload = { ...profile, token: signupToken, password };
      if (payload.title && language === 'de') {
        payload.title = titleMapping[payload.title] || payload.title;
      }
      const endpoint = step === 'claim' ? 'claim' : step === 'pendingSpeaker' ? 'activate-pending' : 'fresh';
      const { data } = await axios.post(`${apiUrl}/api/attendee/public/signup/${endpoint}`, payload);
      storeSession(data);
      // replace, not push: the ?token= URL has just been consumed and would
      // only ever show "this link is no longer valid" if the person went back
      // to it - and leaving it in history would leave the token sitting there.
      navigate('/signup/success', {
        replace: true,
        state: {
          registeredEventName: data.registeredEventName || null,
          registeredEventNameEN: data.registeredEventNameEN || null,
        },
      });
      // Fired after the navigation so the reported page_location is the
      // conversion URL rather than the token-bearing one. A client-side route
      // change does not unload the page, so the queued event is not at risk.
      trackSignup({ registrationType: endpoint, registeredEventName: data.registeredEventName || null });
    } catch (err) {
      setError(err.response?.data || t('signup.error', 'Could not create your account. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = (name, label, type = 'text', required = true, options = null) => {
    if (name === 'phoneNumber') return null;
    return (
      <FormGroup key={name}>
        <Label htmlFor={name}>{label}</Label>
        {type === 'select' ? (
          <Select id={name} name={name} value={profile[name]} onChange={handleFreshChange} required={required}>
            <option value="">{t('form.select', 'Select...')}</option>
            {options && options[language] && options[language].map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
        ) : (
          <Input type={type} id={name} name={name} value={profile[name]} onChange={handleFreshChange} required={required} maxLength={100} />
        )}
      </FormGroup>
    );
  };

  return (
    <PageContainer>
      <Card>
        {step === 'email' && (
          <>
            <Title>{t('signup.title', 'Create your account')}</Title>
            <Subtitle>{t('signup.subtitle', 'Enter your email to get started - if you\'ve registered for a HEARTS event before, we can reuse your details.')}</Subtitle>
            <Form onSubmit={handleStartSignup}>
              <FormGroup>
                <Label htmlFor="email">{t('form.email', 'Email Address')}</Label>
                <Input type="email" id="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </FormGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading ? t('signup.checking', 'Checking…') : t('signup.continue', 'Continue')}
              </SubmitButton>
            </Form>
            <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.875rem' }}>
              {t('signup.haveAccount', 'Already have an account?')} <Link to={eventId ? `/login?eventId=${eventId}` : '/login'}>{t('auth.signIn', 'Sign in')}</Link>
            </div>
          </>
        )}

        {step === 'sent' && (
          <SuccessMessage>
            <p>{t('signup.sent', 'Check your inbox - we\'ve emailed you a link to continue.')}</p>
          </SuccessMessage>
        )}

        {step === 'resolving' && (
          <p style={{ textAlign: 'center' }}>{t('signup.resolving', 'Just a moment…')}</p>
        )}

        {step === 'invalidToken' && (
          <>
            <Title>{t('signup.invalidTokenTitle', 'This link is no longer valid')}</Title>
            <Subtitle>{t('signup.invalidTokenSubtitle', 'It may have expired or already been used. Please start again.')}</Subtitle>
            <SubmitButton type="button" onClick={() => setStep('email')}>
              {t('signup.startOver', 'Start again')}
            </SubmitButton>
          </>
        )}

        {step === 'exists' && (
          <>
            <Title>{t('signup.existsTitle', 'You already have an account')}</Title>
            <Subtitle>{t('signup.existsSubtitle', 'Log in instead to continue.')}</Subtitle>
            <SubmitButton as={Link} to={eventId ? `/login?eventId=${eventId}` : '/login'} style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
              {t('auth.signIn', 'Sign in')}
            </SubmitButton>
          </>
        )}

        {(step === 'claim' || step === 'fresh' || step === 'pendingSpeaker') && (
          <>
            <Title>
              {step === 'claim' ? t('signup.claimTitle', 'Welcome back!')
                : step === 'pendingSpeaker' ? t('signup.pendingSpeakerTitle', 'Set up your account')
                : t('signup.freshTitle', 'Create your account')}
            </Title>
            <Subtitle>
              {step === 'claim'
                ? t('signup.claimSubtitle', "We found your details from a previous registration - please confirm and complete anything that's missing.")
                : step === 'pendingSpeaker'
                ? t('signup.pendingSpeakerSubtitle', "You have a pending speaker invitation for this email, which is separate and still waiting for you. Set up your account now - you can review the speaker invitation whenever you're ready, from your Speaker Hub.")
                : t('signup.freshSubtitle', 'Tell us a bit about yourself.')}
            </Subtitle>
            <Form onSubmit={handleProfileSubmit}>
              <FormRow>
                <FormColumn>{renderField('title', t('form.title', 'Title'), 'select', true, titleOptions)}</FormColumn>
                <FormColumn>{renderField('firstName', t('form.firstName', 'First Name'))}</FormColumn>
                <FormColumn>{renderField('lastName', t('form.lastName', 'Last Name'))}</FormColumn>
              </FormRow>
              <FormGroup>
                <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
                <Input type="password" id="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
              </FormGroup>
              <FormRow>
                <FormColumn>{renderField('institution', t('speaker.field.institution', 'Institution / practice / clinic'))}</FormColumn>
                <FormColumn>{renderField('specialty', t('form.specialization', 'Specialization'), 'select', true, specializationOptions)}</FormColumn>
              </FormRow>
              {renderField('streetAddress', t('form.streetAddress', 'Street Address'))}
              <FormRow>
                <FormColumn>{renderField('postalCode', t('form.postalCode', 'Postal Code'))}</FormColumn>
                <FormColumn>{renderField('city', t('form.city', 'City'))}</FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>{renderField('state', t('form.state', 'State/Province'))}</FormColumn>
                <FormColumn>
                  <FormGroup>
                    <Label htmlFor="country">{t('form.country', 'Country')}</Label>
                    <Select id="country" name="country" value={profile.country} onChange={handleFreshChange} required>
                      <option value="">{t('form.select', 'Select...')}</option>
                      {countryOptions.map((c) => (
                        <option key={c.code} value={c.name}>{c.name}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FormColumn>
              </FormRow>

              <SectionTitle>{t('speaker.onboarding.roles', 'Roles / status')}</SectionTitle>
              <Checks>
                {STATUSES.map((status) => (
                  <CheckLabel key={status}>
                    <input type="checkbox" checked={(profile.professionalStatuses || []).includes(status)} onChange={() => toggleListField('professionalStatuses', status)} />
                    {roleLabel(status)}
                  </CheckLabel>
                ))}
              </Checks>

              <SectionTitle>{t('speaker.onboarding.interests', 'Cardiac interests (choose at least 3)')}</SectionTitle>
              <InterestPicker value={profile.interests} onChange={(v) => setProfile((current) => ({ ...current, interests: v }))} />

              <CheckboxGroup>
                <input type="checkbox" id="consentGiven" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} required />
                <Label htmlFor="consentGiven" style={{ fontWeight: 'normal' }}>
                  {t('form.consentText', 'I agree to the processing of my personal data for the purpose of account creation, event registration, and communication related to my HEARTS account and the events I register for. I understand that I can withdraw my consent at any time.')}
                </Label>
              </CheckboxGroup>
              <CheckboxGroup>
                <input type="checkbox" id="mediaConsentGiven" checked={mediaConsentGiven} onChange={(e) => setMediaConsentGiven(e.target.checked)} required />
                <Label htmlFor="mediaConsentGiven" style={{ fontWeight: 'normal' }}>
                  {t(
                    'form.mediaConsentText',
                    'I acknowledge that photos and video recordings may be taken during the event and may be used for event documentation, communication and promotional purposes across HEARTS/HEARTPORT channels. Further information is available in our {privacyPolicyLink}.',
                    { privacyPolicyLink: <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">{t('footer.privacyPolicy', 'Privacy Policy')}</a> }
                  )}
                </Label>
              </CheckboxGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading
                  ? t('signup.creating', 'Creating account…')
                  : step === 'claim'
                    ? t('signup.claimSubmit', 'Confirm & create account')
                    : t('signup.create', 'Create account')}
              </SubmitButton>
            </Form>
          </>
        )}

      </Card>
    </PageContainer>
  );
};

export default AttendeeSignup;
